import { Link } from "react-router-dom";
import { Github, Linkedin, Mail, LucideIcon } from "lucide-react";
import { useSiteSettings, SocialLink } from "@/hooks/useSiteSettings";
import { HtmlContent } from "@/components/ui/html-content";
import { withStoreLink } from "@/lib/nav-links";

const iconMap: Record<string, LucideIcon> = {
  Github,
  Linkedin,
  Mail,
};

export function Footer() {
  

  const getSocialIcon = (iconName: string): LucideIcon => {
    return iconMap[iconName] || Mail;
  };

  const { footerSettings, headerSettings } = useSiteSettings();

  // Parse logo - handle both "Nathan Stoker" and "NathanStoker" formats
  const logoText = headerSettings.logo;
  let firstName = "";
  let lastName = "";
  
  if (logoText.includes(" ")) {
    const parts = logoText.split(" ");
    firstName = parts[0];
    lastName = parts.slice(1).join(" ");
  } else {
    const match = logoText.match(/^([A-Z][a-z]+)(.*)$/);
    if (match) {
      firstName = match[1];
      lastName = match[2];
    } else {
      firstName = logoText;
    }
  }

  return (
    <footer className="bg-card border-t border-border">
      <div className="container mx-auto py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block">
              <span className="text-2xl font-bold text-foreground">
                {firstName}{lastName && <><span className="text-foreground"> </span><span className="text-primary">{lastName}</span></>}
              </span>
            </Link>
            <HtmlContent 
              content={footerSettings.brandDescription}
              className="mt-4 text-muted-foreground max-w-md [&_p]:m-0"
            />
            <div className="flex space-x-4 mt-6">
              {footerSettings.socialLinks.map((social: SocialLink) => {
                const IconComponent = getSocialIcon(social.icon);
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                    aria-label={social.label}
                  >
                    <IconComponent size={20} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Links */}
          {footerSettings.footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="font-semibold text-foreground mb-4">{section.title}</h3>
              <ul className="space-y-3">
                {(section.title === "Navigation" ? withStoreLink(section.links) : section.links).map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} {footerSettings.copyrightText}
          </p>
          <div className="flex items-center gap-4">
            <Link 
              to="/sitemap" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Sitemap
            </Link>
            <p className="text-sm text-muted-foreground">
              {footerSettings.builtWithText}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}